import { onScopeDispose, useHost, watch } from 'vue'

/**
 * Resolves the custom element host node of the current widget.
 * Must be called during `setup()` of a component defined with
 * `defineCustomElement`. The host is known from the start; the component's
 * own root element exists only once it has mounted, so reading it here
 * found nothing and no event ever left the widget.
 *
 * Returns a function that dispatches CustomEvents from the host element,
 * safe to call from watchers, event handlers, or any later context.
 */
export function useWidgetEvents() {
    const host = useHost('useWidgetEvents')

    /**
     * Dispatches a CustomEvent from the custom element's host node so it
     * bubbles out of the shadow DOM and is observable by the host page.
     */
    function emit(name: string, detail?: Record<string, unknown>): void {
        host?.dispatchEvent(
            new CustomEvent(name, {
                detail: detail ?? null,
                bubbles: true,
                composed: true,
            }),
        )
    }

    return { emit }
}

/**
 * Mirrors the agent's working state on the custom element (spec
 * `widget-copilot.md`, D4): the `working` attribute while it lasts, for a
 * page that draws its own effect in CSS, and the `working-started` and
 * `working-ended` events at its two edges. Like `useWidgetEvents`, it must
 * be called during `setup()`.
 *
 * The attribute starts from the state, so an element put back in the page
 * drops one it kept; and an element unmounted while working ends the
 * signal, or a page waiting for `working-ended` would wait for ever.
 */
export function useWorkingSignal(isWorking: () => boolean): void {
    useStateSignal(isWorking, 'working', 'working-started', 'working-ended')
}

/**
 * Mirrors the agent acting on the page (spec `page-tools.md`) the same way:
 * the `controlling` attribute while the control bar and its stop show, and
 * `page-control-started` and `page-control-ended`. It is what lights an
 * effect around the page, where `working` also covers an answer that only
 * thinks and writes.
 */
export function usePageControlSignal(isControlling: () => boolean): void {
    useStateSignal(
        isControlling,
        'controlling',
        'page-control-started',
        'page-control-ended',
    )
}

function useStateSignal(
    isOn: () => boolean,
    attribute: string,
    started: string,
    ended: string,
): void {
    const host = useHost('useStateSignal')
    const { emit } = useWidgetEvents()
    let signalled = false
    const signal = (on: boolean) => {
        host?.toggleAttribute(attribute, on)
        if (on !== signalled) {
            signalled = on
            emit(on ? started : ended)
        }
    }
    watch(isOn, signal, { immediate: true })
    onScopeDispose(() => signal(false))
}
